"use client";

import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerClose,
  Close,
} from "@oreme/ui";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import React, { type HTMLAttributes, type ReactElement } from "react";
import {
  LabelList,
  TagList,
  PersonList,
  ObservatoryTheme,
  ObservatoryStatList,
} from "@/components";
import type { ThemeTypeEnum, Observatory } from "@/types";

interface ObservatoryDrawerProps extends HTMLAttributes<HTMLDivElement> {
  observatory: Observatory;
}

function ObservatoryDrawer({
  observatory,
}: Readonly<ObservatoryDrawerProps>): ReactElement {
  const locale = useLocale();
  const t = useTranslations("global.ObservatoryDrawer");

  const url =
    observatory.hasData && observatory.portalURL
      ? observatory.portalURL
      : observatory.corporateURL;

  return (
    <Drawer direction="right">
      <DrawerTrigger className="after:absolute after:inset-0 after:z-10 hover:cursor-pointer">
        <span className="sr-only">{t("action")}</span>
      </DrawerTrigger>
      <DrawerContent
        className="fixed inset-0 left-auto w-full! lg:max-w-110! h-dvh flex flex-col rounded-none p-0 m-0"
        data-vaul-no-drag
      >
        <DrawerHeader className="relative text-left leading-tight">
          <div className="mb-4">
            <ObservatoryTheme theme={observatory.theme as ThemeTypeEnum} />
          </div>
          <DrawerTitle className="text-2xl">
            {observatory.name[locale]}
          </DrawerTitle>
          {observatory.description ? (
            <DrawerDescription className="-mt-1">
              {observatory.description[locale]}
            </DrawerDescription>
          ) : null}
          <DrawerClose className="absolute top-4 right-3">
            <Button size="icon" variant="ghost" aria-label={t("close")} asChild>
              <Close title={t("close")} />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="flex-1 min-h-0">
          <div className="h-full overflow-y-auto [-webkit-overflow-scrolling:touch]">
            <aside className="flex-1 flex flex-col">
              <figure className="relative bg-gray-700 flex justify-center items-center">
                <Image
                  alt={observatory.name[locale]}
                  height={960}
                  src={observatory.picture!}
                  width={1440}
                />
                {observatory.labels?.length ? (
                  <LabelList
                    data={observatory.labels}
                    className="absolute top-4 left-4 right-0"
                  />
                ) : null}
              </figure>
              <div className="flex flex-col">
                <ObservatoryStatList observatory={observatory} />
                {observatory.tags?.length ? (
                  <section className="flex flex-col gap-2 p-4">
                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                      {t("tags")}
                    </h3>
                    <TagList data={observatory.tags} />
                  </section>
                ) : null}
                {observatory.persons?.length ? (
                  <section className="flex flex-col gap-2 p-4">
                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                      {t("leadership")}
                    </h3>
                    <PersonList data={observatory.persons} />
                  </section>
                ) : null}
              </div>
            </aside>
          </div>
        </div>
        {url ? (
          <DrawerFooter className="flex border-t bg-primary-foreground">
            <Link className="flex lg:self-end" href={url}>
              <Button className="flex-1 flex flex-row gap-2" color="blue">
                {t("action")} <ArrowRight />
              </Button>
            </Link>
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

export { ObservatoryDrawer, type ObservatoryDrawerProps };
